import { useEffect, useRef } from "react";

/** Dev-only "fail loud" guard for Client-Portal-only presentational components (pages/
 * clientPortal/*.jsx) that depend on `.cp-theme`-scoped CSS variables (clientPortal.css) — added
 * after a real bug (2026-02): TimelineRail.jsx got reused directly on a staff page, where those
 * variables silently resolve to nothing, rendering invisible (white-on-white) text with zero
 * warning. Attach the returned ref to the component's OUTERMOST DOM node; in development, on
 * mount it walks up for a `.cp-theme` ancestor and — if missing — throws a loud, unmistakable
 * console.error naming the component, instead of silently rendering broken. No-op in
 * production (never affects real users, never changes what they see). */
export function useClientThemeGuard(componentName) {
  const ref = useRef(null);
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (ref.current && !ref.current.closest(".cp-theme")) {
      // eslint-disable-next-line no-console
      console.error(
        `[client-theme-guard] <${componentName}> is a Client-Portal-only component (depends on ` +
        `.cp-theme CSS variables — see pages/clientPortal/clientPortal.css) but was rendered ` +
        `OUTSIDE a .cp-theme wrapper. Its colours will silently resolve to nothing (e.g. ` +
        `invisible white-on-white text) — do not reuse this component on a staff page. Build a ` +
        `staff-only equivalent instead (see components/project/StaffTimelineRail.jsx for the ` +
        `fix applied to this exact bug on the Project Overview tab).`
      );
    }
  }, [componentName]);
  return ref;
}
